// Dependencies
import React from "react";
import {
  Grid,
  Pagination,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

// Redux
import {
  MarketsState,
  setMarketTablePage,
  setTriggerRefetch,
  useMarketsState,
} from "src/store/features/markets.slice";
import { RootState, useAppDispatch, useAppSelector } from "src/store/store";

// Components
import PaginationMobile from "src/components/markets/Table/PaginationMobile";
import { MarketTableStyles } from "src/components/markets/Table/styles/MarketTable.styles";
import { MarketsTableSkeleton } from "src/components/common/Skeletons";
import MarketTableBody from "./styles/MarketTableBody";
import MarketTableError from "./styles/MarketTableError";

// Imported Types
import { Column } from "src/components/markets/Table/types";

// API
import api from "src/store/features/api";

// Table head
const TABLE_HEAD: readonly Column[] = [
  {
    id: "watch",
    label: "",
    minWidth: 10,
  },
  {
    id: "rank",
    label: "#",
    minWidth: 10,
  },
  {
    id: "logo",
    label: "Coin",
    minWidth: 10,
  },
  {
    id: "name",
    label: "Name",
    minWidth: 10,
  },
  {
    id: "price",
    label: "Price",
    minWidth: 10,
  },
  {
    id: "24h",
    label: "24h",
    minWidth: 100,
  },
  {
    id: "change7d",
    label: "7d",
    minWidth: 100,
  },
  {
    id: "change30d",
    label: "30d",
    minWidth: 100,
  },
  {
    id: "marketCap",
    label: "Market cap",
    minWidth: 10,
  },
  {
    id: "chart",
    label: "Last 7 days",
    minWidth: 10,
  },
  {
    id: "alert",
    label: "",
    minWidth: 10,
  },
];

export type PaginationParams = { limit: number; page: number };

// Established Market Types for the MarketTable component
type MarketType = "all" | "watchlist" | "portfolio" | "recent" | "gainers";

const MarketTable = (): JSX.Element => {
  // STYLES
  const classes = MarketTableStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  //REDUX
  const reduxState = useAppSelector((state: RootState): MarketsState => state.markets);
  const dispatch = useAppDispatch();
  const { page } = useMarketsState();

  const DEFAULT_PAGINATION_PARAMS: PaginationParams = {
    limit: 10,
    page: page,
  };
  const [paginationParams, setPaginationParams] =
    React.useState<PaginationParams>(DEFAULT_PAGINATION_PARAMS);

  // Ensure reduxState.type is a MarketType
  if (!isMarketType(reduxState.type)) {
    throw new Error(`Invalid type: ${reduxState.type}`);
  }

  // Define query options directly
  const queryOptions = {
    type: reduxState.type,
    sector: reduxState.sector,
    ...paginationParams,
  };

  // Check if we need to skip the query based on the reduxState.type
  const shouldSkip = reduxState.type !== queryOptions.type;

  // Use the query hook at the top level
  let marketData = api.useGetMarketTokensListQuery(queryOptions, {
    skip: shouldSkip,
    pollingInterval: 60000,
  });

  // Helper function to check if a string is a MarketType
  function isMarketType(type: string): type is MarketType {
    return ["all", "watchlist", "portfolio", "recent", "gainers"].includes(type);
  }

  // Refetch data if user likes/dislikes token to avoid stagnate data. Use debouce to decrease the amount
  // of requests if the user is spam liking/disliking
  React.useEffect((): void => {
    marketData.refetch();
    dispatch(setTriggerRefetch(false));
  }, [reduxState.triggerRefetch]);

  // Always refetch watchlist data whenever user selects the watchlist table tab.
  // This is to ensure that the watchlist is always up to date.
  // Reset pagination
  React.useEffect((): void => {
    if (reduxState.type === "watchlist") marketData.refetch();
    setPaginationParams(DEFAULT_PAGINATION_PARAMS);
  }, [reduxState.type]);

  // CHANGING TABLE PAGE - USING REDUX AS STATE IS SHARED ACROSS COMPONENTS
  const handleChangePage = (event: unknown, newPage: number): void => {
    setPaginationParams((prev: PaginationParams): PaginationParams => ({ ...prev, page: newPage }));
    dispatch(setMarketTablePage(newPage));
  };

  return (
    <>
      <TableContainer>
        <Table stickyHeader>
          {/* TABLE HEAD */}
          <TableHead
            style={{
              position: "sticky",
              top: "0px",
              backgroundColor: "#FFF",
              zIndex: 99,
            }}>
            <TableRow>
              {TABLE_HEAD.map(
                (column: Column): JSX.Element => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      borderBottom: `0.5px solid ${theme.palette.primary.main}`,
                    }}>
                    {column.label}
                  </TableCell>
                ),
              )}
            </TableRow>
          </TableHead>
          {/* TABLE BODY */}
          {marketData.isFetching ? (
            <MarketsTableSkeleton />
          ) : (
            marketData?.data && (
              <MarketTableBody
                isFetching={marketData.isFetching}
                isSuccess={marketData.isSuccess}
                data={marketData.data}
                reduxState={reduxState}
              />
            )
          )}
        </Table>
      </TableContainer>
      <Grid
        container
        alignItems="center"
        justifyContent={isMobile ? "" : "center"}
        item
        className={classes.stack}>
        {isMobile ? (
          <PaginationMobile
            count={marketData.data?.totalTokens ?? 1}
            page={marketData.data?.currentPage ?? 1}
            current={marketData.data?.currentPage ?? 1}
            onChange={handleChangePage}
          />
        ) : (
          <Pagination
            className={classes.pagination}
            count={marketData.data?.totalPages}
            page={marketData.data?.currentPage ?? 1}
            onChange={handleChangePage}
            color="secondary"
            showFirstButton
            showLastButton
          />
        )}
      </Grid>
      {!marketData.isSuccess && !marketData.isFetching && (
        <MarketTableError reduxState={reduxState} />
      )}
    </>
  );
};

export default MarketTable;
